import React, { useCallback, useState } from "react";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { message, Pagination } from "antd5";
import classNames from "classnames";

import RequestContactsButton from "components/actions/RequestContactsButton";
import { DetailsContent } from "components/app_layout/DetailsLayout";
import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import RedactedWrapper, { RedactedLink } from "lib/core_components/RedactedWrapper";
import SkeletonTable from "lib/core_components/SkeletonTable";
import { Table } from "lib/core_components/Table";
import { ColumnSizeConfig, ColumnType } from "lib/core_components/Table/ColumnTypes";
import { tableRowSelection } from "lib/core_components/Table/Table";
import TableBanner from "lib/core_components/TableBanner";
import { createUseDebounce, useDebouncedValue } from "lib/debounce";
import { ContactResult, useContacts } from "lib/hooks/api/contacts/useContacts";
import { useUpdateRecordQualification } from "lib/hooks/api/records/useUpdateRecordQualification";
import { useURLState } from "lib/hooks/useURLState";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { PaginationState } from "lib/search/types";
import { TrackingProvider } from "lib/tracking";
import { getSignalTypes, RecordDetails } from "lib/types/models";
import { PaywallBanner } from "../paywall/PaywallBanner";
import { ContactsFilterBar } from "./ContactsFilterBar";
import {
  BuyerDetailsCell,
  ContactInformationCell,
  ContactNameCell,
  ContactsTableEmpty,
} from "./ContactsTableUtils";
import {
  ContactFilters,
  convertFiltersToSearchContactsRequest,
  DEFAULT_CONTACT_FILTERS,
  parseContactsFilters,
} from "./contactUtils";

import css from "./ContactsTable.module.scss";

import ContactSvg from "../../../assets/images/contact.svg";

const CONTACTS_DATA_TYPE = "CONTACTS";

const BASIC_SIZE: ColumnSizeConfig = {
  small: 150,
  medium: 150,
  large: 175,
  xlarge: 350,
};

const MEDIUM_SIZE: ColumnSizeConfig = {
  small: 200,
  medium: 225,
  large: 225,
  xlarge: 500,
};

const LARGE_SIZE: ColumnSizeConfig = {
  small: 350,
  medium: 350,
  large: 350,
  xlarge: 500,
};

type ContactsTableProps = {
  // If this prop is provided, then contacts are searched for already within thr context of a buyer
  buyerId?: string;
  addContacts?: (contacts: ContactResult[]) => void;

  // Want to make this prop required so we have complete tracking of this reusable component
  contextSource: string;
  pageSize?: number;

  showAllFiltersDrawer: boolean;
  showDropdownFilters: boolean;

  record?: RecordDetails;
};

function ContactsTable({
  buyerId,
  addContacts,
  contextSource,
  pageSize = 20,
  showAllFiltersDrawer,
  showDropdownFilters,
  record,
}: ContactsTableProps) {
  const { authorised: hasContacts } = useCheckSubscription("CONTACTS", {
    "Context source": contextSource,
  });

  const useDebounce300 = createUseDebounce(300);

  // Only used if within the context of a record (RelatedContactsPage component)
  const { mutate: updateRecordQualification } = useUpdateRecordQualification({
    onError: () => message.error("Error updating record qualification"),
  });

  const [pagingState, setPagingState] = useState<PaginationState>({
    current: 1,
    pageSize: pageSize,
  });

  // If we are within the context of a buyer, then the default filters must always reflect this
  const defaultContactFilters: ContactFilters = {
    ...DEFAULT_CONTACT_FILTERS,
    buyerGuids: buyerId ? [buyerId] : [],
  };

  const [contactFilters = defaultContactFilters, setContactFilters] = useURLState<ContactFilters>(
    "contactsFilters",
    defaultContactFilters,
    (params) => parseContactsFilters(params, defaultContactFilters),
  );

  const [selectedRowGuids, setSelectedRowGuids] = useState<string[]>([]);

  const [debouncedContactFilters] = useDebouncedValue<ContactFilters>(contactFilters);

  const [showTooltipRowIndex, setShowTooltipRowIndex] = useState<number>();

  const {
    data: contactsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useContacts(
    {
      ...convertFiltersToSearchContactsRequest(
        debouncedContactFilters,
        pagingState.pageSize,
        pagingState.current,
      ),
    },
    {
      enabled: hasContacts,
    },
  );

  /**
   * If within the context of a record (in RelatedContacts component) then this callback will update the qualfication status of that record,
   * will also move the contact to actioned (when we add that functionality)
   */
  const updateStatus = useCallback(() => {
    if (record) {
      updateRecordQualification({
        recordGuid: record.guid,
        score: record.relevance_score,
        signalTypes: getSignalTypes(record.signals),
        procurementStage: {
          id: record.procurement_stage.id,
          stage: record.procurement_stage.stage,
        },
        qualification: "pre_engage_done",
        contextSource,
      });
    }

    // TODO: Add code to update contact to "Actioned" here when we add that in
  }, [record, updateRecordQualification, contextSource]);

  const onChangeSearchFilters = useDebounce300((filters: Partial<ContactFilters>) => {
    setContactFilters((oldFilters) => ({ ...oldFilters, ...filters }));
    setPagingState((oldPagingState) => ({ ...oldPagingState, currentPage: 1 }));
  });

  const columns = React.useMemo((): ColumnType<ContactResult>[] => {
    const columns: ColumnType<ContactResult>[] = [
      {
        key: "name",
        title: "Job title & Contact name",
        render: (t: unknown, c: ContactResult, index: number) => (
          <ContactNameCell
            contact={c}
            record={record}
            rowIndex={index}
            showTooltipRowIndex={showTooltipRowIndex}
            setShowTooltipRowIndex={setShowTooltipRowIndex}
            showFirstCellActions={!!record}
          />
        ),
        sizeConfig: LARGE_SIZE,
      },
      {
        title: <EllipsisTooltipText fullText="Contact information" />,
        key: "contactInformation",
        sizeConfig: MEDIUM_SIZE,
        render: (_: unknown, c: ContactResult) => (
          <RedactedWrapper
            requiredDataType={CONTACTS_DATA_TYPE}
            featureType={window.guestUser ? "GUEST_ACCESS_UPGRADE" : undefined}
            contextSource="In-row"
            redactContent={
              <div className={css.contactCellContent}>
                <span className={css.contactDetail}>
                  <MailOutlined className={css.contactIcon} />
                  <RedactedLink textToRedact={c.email || "john@fakemail.com"} />
                </span>
                <span className={css.contactDetail}>
                  <PhoneOutlined className={css.contactIcon} />
                  <RedactedLink textToRedact={c.phone || "123-456 78911"} />
                </span>
              </div>
            }
          >
            <ContactInformationCell contact={c} onCopy={updateStatus} />
          </RedactedWrapper>
        ),
      },
      {
        title: "Seniority",
        key: "seniority",
        dataIndex: "seniority",
        sizeConfig: BASIC_SIZE,
        render: (_: unknown, c: ContactResult) => (
          <EllipsisTooltipText fullText={c.seniority} containerClassname={css.elipsisText} />
        ),
      },
      {
        title: "Function",
        key: "function",
        dataIndex: "jobFunction",
        sizeConfig: BASIC_SIZE,
        render: (_: unknown, c: ContactResult) => (
          <EllipsisTooltipText
            fullText={c.jobFunction.join(", ")}
            containerClassname={css.elipsisText}
          />
        ),
      },
    ];

    if (!buyerId) {
      columns.splice(2, 0, {
        title: "Buyer",
        key: "buyerGuid",
        render: (_: unknown, c: ContactResult) => {
          if (!c.buyer) {
            return <span>{c.oscarOrgName}</span>;
          }

          return <BuyerDetailsCell contact={c} />;
        },
        sizeConfig: MEDIUM_SIZE,
      });
    }

    return columns;
  }, [buyerId, record, showTooltipRowIndex, updateStatus]);

  if (!hasContacts || (error instanceof Error && error.message.includes("Forbidden resource"))) {
    return <PaywallBanner featureType="CONTACTS" />;
  }

  return (
    <TrackingProvider
      data={{
        "Context source": contextSource,
      }}
    >
      <span>
        {addContacts && (
          <Button
            buttonStyle="destructive"
            disabled={selectedRowGuids.length === 0}
            onClick={() =>
              addContacts(
                contactsData?.contacts.results.filter((x) => selectedRowGuids.includes(x.id)) || [],
              )
            }
          >
            Add Contacts ({selectedRowGuids.length})
          </Button>
        )}
        <ContactsFilterBar
          contactFilters={contactFilters}
          onChangeSearchFilters={onChangeSearchFilters}
          selectedRowGuids={selectedRowGuids}
          totalResults={contactsData?.contacts.totalResults ?? 0}
          hideBuyerFilters={!!buyerId}
          defaultFilters={defaultContactFilters}
          showAllFiltersDrawer={showAllFiltersDrawer}
          showDropdownFilters={showDropdownFilters}
          paginationState={pagingState}
        />
      </span>
      <DetailsContent
        className={classNames({
          [css.removeSpacing]: !!buyerId,
        })}
      >
        {isLoading ? (
          <SkeletonTable
            columns={columns}
            pageSize={pagingState.pageSize}
            rowCount={pagingState.pageSize}
            active
          />
        ) : contactsData?.contacts && contactsData.contacts.results.length > 0 ? (
          <>
            <Table<ContactResult>
              ariaLabel="Contacts Table"
              columns={columns}
              dataSource={contactsData?.contacts.results || []}
              loading={isFetching}
              className={css.hiddenOverflow}
              rowKey={(row) => row.id}
              pagination={false}
              rowSelection={tableRowSelection(
                selectedRowGuids,
                setSelectedRowGuids,
                contactsData?.contacts.results || [],
                "id",
              )}
              isError={isError}
            />
            {!window.guestUser && (
              <TableBanner
                title="Not found any relevant contacts?"
                description="Just send a request below and a member of the team can look into a couple of other sources for you."
                graphic={<img src={ContactSvg} alt="Contact" className={css.bannerImg} />}
                ctaElement={
                  <RequestContactsButton
                    contactFilters={contactFilters}
                    contextSource="No relevant contacts banner"
                  />
                }
              />
            )}
            <Pagination
              className={css.pagination}
              current={pagingState.current}
              defaultPageSize={pagingState.pageSize}
              total={contactsData?.contacts.totalResults ? contactsData.contacts.totalResults : 0}
              onChange={(current: number, pageSize: number) => {
                setPagingState({ current, pageSize });
              }}
            />
          </>
        ) : (
          <ContactsTableEmpty contactFilters={contactFilters} />
        )}
      </DetailsContent>
    </TrackingProvider>
  );
}

export default ContactsTable;
